import { Routes, Route, Navigate, Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, createContext, useContext } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { masterApi, apiError } from "@/lib/api";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { modal } from "@/lib/modal";
import {
  BarChart3, Users, Percent, Wallet, FileBarChart, Contact, MessageCircle, Send, LogOut, Settings, Star, Activity, Car, Clock, Building2, Plug, ChevronDown, ScrollText, Database, MessageSquareText,
  Users2, Droplets, Ticket,
} from "lucide-react";
import logoGrupo from "@/assets/logo-grupo.png";
import MasterDashboard from "@/pages/Master/MasterDashboard";
import MasterVendedores from "@/pages/Master/MasterVendedores";
import MasterDescontos from "@/pages/Master/MasterDescontos";
import MasterAppDriver from "@/pages/Master/MasterAppDriver";
import MasterCaixa from "@/pages/Master/MasterCaixa";
import MasterRelatorios from "@/pages/Master/MasterRelatorios";
import MasterClientes from "@/pages/Master/MasterClientes";
import MasterWhatsApp from "@/pages/Master/MasterWhatsApp";
import MasterSms from "@/pages/Master/MasterSms";
import MasterCampanhas from "@/pages/Master/MasterCampanhas";
import MasterPesquisas from "@/pages/Master/MasterPesquisas";
import MasterAnalises from "@/pages/Master/MasterAnalises";
import MasterConfig from "@/pages/Master/MasterConfig";
import MasterIntegracoes from "@/pages/Master/MasterIntegracoes";
import MasterLogs from "@/pages/Master/MasterLogs";
import MasterBackup from "@/pages/Master/MasterBackup";
import MasterGruposConsumo from "@/pages/Master/MasterGruposConsumo";
import MasterTrocaOleo from "@/pages/Master/MasterTrocaOleo";
import MasterSorteio from "@/pages/Master/MasterSorteio";

export const UnitCtx = createContext(null);
export const useUnit = () => useContext(UnitCtx);

// Menu organizado em grupos: Cadastro, Gestão, Sistema.
const MENU = [
  { items: [
    { path: "/master/dashboard", label: "Dashboard", slug: "dashboard", icon: BarChart3 },
  ] },
  { label: "Cadastro", items: [
    { path: "/master/vendedores", label: "Vendedores", slug: "vendedores", icon: Users },
    { path: "/master/clientes", label: "Clientes", slug: "clientes", icon: Contact },
    { path: "/master/grupos-consumo", label: "Grupos de Consumo", slug: "grupos-consumo", icon: Users2 },
    { path: "/master/descontos", label: "Descontos (Normal)", slug: "descontos", icon: Percent },
    { path: "/master/app-driver", label: "Motorista de App", slug: "app-driver", icon: Car },
    { path: "/master/campanhas", label: "Campanhas", slug: "campanhas", icon: Send },
  ] },
  { label: "Gestão", items: [
    { path: "/master/caixa", label: "Caixa", slug: "caixa", icon: Wallet },
    { path: "/master/relatorios", label: "Relatórios", slug: "relatorios", icon: FileBarChart },
    { path: "/master/analises", label: "Análises", slug: "analises", icon: Activity },
    { path: "/master/pesquisas", label: "Pesquisas", slug: "pesquisas", icon: Star },
    { path: "/master/troca-oleo", label: "Troca de Óleo", slug: "troca-oleo", icon: Droplets },
    { path: "/master/sorteio", label: "Sorteio", slug: "sorteio", icon: Ticket },
  ] },
  { label: "Sistema", items: [
    { path: "/master/integracoes", label: "Integrações", slug: "integracoes", icon: Plug },
    { path: "/master/whatsapp", label: "WhatsApp", slug: "whatsapp", icon: MessageCircle },
    { path: "/master/sms", label: "SMS", slug: "sms", icon: MessageSquareText },
    { path: "/master/logs", label: "Logs de Acesso", slug: "logs", icon: ScrollText },
    { path: "/master/backup", label: "Backup", slug: "backup", icon: Database },
    { path: "/master/config", label: "Configuração", slug: "config", icon: Settings },
  ] },
];

export default function MasterApp() {
  const { logoutMaster } = useAuth();
  const [unidades, setUnidades] = useState([]);
  const [unitId, setUnitId] = useState(localStorage.getItem("master_unit_id") || "all");
  const loc = useLocation();
  const nav = useNavigate();
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  const dataCurta = now.toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "short" });
  const hora = now.toLocaleTimeString("pt-BR");

  useEffect(() => {
    masterApi.get("/unidades").then((r) => setUnidades(r.data))
      .catch((e) => modal.error(apiError(e)));
  }, []);
  useEffect(() => { localStorage.setItem("master_unit_id", unitId); }, [unitId]);

  const isActive = (p) => loc.pathname.startsWith(p);

  // Menu sanfona: grupos iniciam retraídos; o grupo da rota ativa abre sozinho.
  const activeGroupLabel = MENU.find((g) => g.label && g.items.some((it) => isActive(it.path)))?.label;
  const [openGroups, setOpenGroups] = useState({});
  useEffect(() => {
    if (activeGroupLabel) setOpenGroups((p) => (p[activeGroupLabel] ? p : { ...p, [activeGroupLabel]: true }));
  }, [activeGroupLabel]);
  const toggleGroup = (label) => setOpenGroups((p) => ({ ...p, [label]: !p[label] }));

  return (
    <UnitCtx.Provider value={{ unitId, setUnitId, unidades }}>
      <div className="min-h-screen flex" data-testid="master-app">
        {/* SIDEBAR — slim em mobile (icon only), completa em desktop */}
        <aside className="shrink-0 sticky top-0 h-screen p-2 md:p-3 w-[64px] md:w-[220px] transition-all">
          <div className="glass rounded-xl h-full flex flex-col py-3 px-1.5 md:px-2.5">
            {/* LOGO acima do menu */}
            <div className="mb-2.5">
              <div className="bg-white rounded-lg border border-[var(--line)] shadow-sm flex items-center justify-center px-1.5 py-2 md:px-3 md:py-2.5">
                <img src={logoGrupo} alt="Grupo Laçador" className="h-7 md:h-10 w-auto object-contain" data-testid="sidebar-logo" />
              </div>
              <div className="hidden md:block text-center mt-1.5 text-[9px] uppercase tracking-[0.22em] text-neutral-500">Postos Fidelidade</div>
            </div>

            <nav className="flex-1 overflow-y-auto py-1">
              {MENU.map((group, gi) => {
                const items = (
                  <div className="space-y-0.5">
                    {group.items.map((t) => {
                      const Icon = t.icon;
                      const active = isActive(t.path);
                      return (
                        <Link key={t.path} to={t.path}
                              data-testid={`master-tab-${t.slug}`}
                              title={t.label}
                              className={`flex items-center gap-2.5 px-2 md:px-2.5 h-9 rounded-lg text-[12px] font-medium transition-colors
                                        ${active ? "bg-neutral-900 text-white shadow-sm" : "text-neutral-700 hover:bg-white/70"}`}>
                          <Icon size={14} className="shrink-0" />
                          <span className="hidden md:inline truncate">{t.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                );
                // Grupo sem rótulo (Dashboard): sempre visível.
                if (!group.label) return <div key={gi}>{items}</div>;
                const open = !!openGroups[group.label];
                return (
                  <div key={gi} className="mt-1.5">
                    {/* Cabeçalho clicável (desktop) — sanfona com seta */}
                    <button type="button" onClick={() => toggleGroup(group.label)}
                            data-testid={`master-group-${gi}`}
                            className="hidden md:flex w-full items-center justify-between px-2.5 h-8 rounded-lg text-[9px] uppercase tracking-[0.18em] text-neutral-500 font-semibold hover:bg-white/70 transition-colors">
                      <span>{group.label}</span>
                      <ChevronDown size={13} className={`shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
                    </button>
                    {/* Divisor em mobile (sidebar só com ícones) */}
                    <div className="md:hidden mx-1.5 my-1.5 h-px bg-[var(--line)]" />
                    {/* Itens: sempre visíveis em mobile; colapsáveis em desktop */}
                    <div className={`mt-0.5 ${open ? "" : "md:hidden"}`}>
                      {items}
                    </div>
                  </div>
                );
              })}
            </nav>

            <button data-testid="logout-master-btn"
                    onClick={() => { logoutMaster(); nav("/"); }}
                    title="Sair"
                    className="btn-ghost h-9 mt-1.5 font-medium flex items-center justify-center md:justify-start gap-2 px-2 md:px-2.5 text-[12px]"
                    style={{ borderRadius: 10 }}>
              <LogOut size={13} className="shrink-0" />
              <span className="hidden md:inline">Sair</span>
            </button>
          </div>
        </aside>

        {/* MAIN */}
        <div className="flex-1 min-w-0 flex flex-col">
          {/* HEADER — cards de info alinhados à ESQUERDA */}
          <header className="sticky top-0 z-30 px-4 md:px-6 py-3 flex items-center gap-2
                            backdrop-blur-xl bg-[rgba(244,245,247,0.7)] border-b border-white/40">
            {/* DATA/HORA ao vivo */}
            <div className="hidden sm:flex glass rounded-xl px-3.5 h-11 items-center gap-2.5" data-testid="header-clock">
              <Clock size={15} className="text-[color:var(--brand-blue)] shrink-0" />
              <div className="leading-tight">
                <div className="text-[10px] uppercase tracking-wider text-neutral-500 capitalize">{dataCurta}</div>
                <div className="font-mono-ibm text-[13px] font-semibold number-tabular text-[color:var(--ink)]">{hora}</div>
              </div>
            </div>
            {/* SELETOR DE FILIAL */}
            <Select value={unitId} onValueChange={setUnitId}>
              <SelectTrigger data-testid="unit-selector"
                             className="w-[170px] md:w-[215px] h-11 rounded-xl border border-[var(--line)] bg-white shadow-sm px-2.5 py-0 gap-1.5 overflow-hidden">
                <div className="flex items-center gap-2 min-w-0 flex-1 text-left">
                  <span className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: "rgba(10,42,115,0.10)" }}>
                    <Building2 size={15} className="text-[color:var(--brand-blue)]" />
                  </span>
                  <span className="flex flex-col leading-none gap-0.5 min-w-0">
                    <span className="text-[9px] uppercase tracking-wider text-neutral-400">Unidade</span>
                    <span className="text-[12px] font-semibold text-[color:var(--ink)] truncate"><SelectValue /></span>
                  </span>
                </div>
              </SelectTrigger>
              <SelectContent className="rounded-xl border-none glass">
                <SelectItem value="all">Todas as Unidades</SelectItem>
                {unidades.map((u) => <SelectItem key={u.id} value={u.id}>{u.nome}</SelectItem>)}
              </SelectContent>
            </Select>
            {/* USUÁRIO */}
            <div className="hidden lg:flex items-center gap-2 glass rounded-xl pl-1.5 pr-3 h-11">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold shrink-0"
                   style={{ background: "linear-gradient(135deg,#0A2A73,#1660D6)" }}>M</div>
              <div className="leading-tight">
                <div className="text-[9px] uppercase tracking-wider text-neutral-400">Usuário</div>
                <div className="text-xs font-semibold text-[color:var(--ink)]">Master</div>
              </div>
            </div>
          </header>

          <main className="flex-1">
            <Routes>
              <Route path="/" element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<MasterDashboard />} />
              <Route path="vendedores" element={<MasterVendedores />} />
              <Route path="descontos" element={<MasterDescontos />} />
              <Route path="app-driver" element={<MasterAppDriver />} />
              <Route path="caixa" element={<MasterCaixa />} />
              <Route path="relatorios" element={<MasterRelatorios />} />
              <Route path="analises" element={<MasterAnalises />} />
              <Route path="clientes" element={<MasterClientes />} />
              <Route path="grupos-consumo" element={<MasterGruposConsumo />} />
              <Route path="troca-oleo" element={<MasterTrocaOleo />} />
              <Route path="sorteio" element={<MasterSorteio />} />
              <Route path="whatsapp" element={<MasterWhatsApp />} />
              <Route path="sms" element={<MasterSms />} />
              <Route path="campanhas" element={<MasterCampanhas />} />
              <Route path="pesquisas" element={<MasterPesquisas />} />
              <Route path="integracoes" element={<MasterIntegracoes />} />
              <Route path="logs" element={<MasterLogs />} />
              <Route path="backup" element={<MasterBackup />} />
              <Route path="config" element={<MasterConfig />} />
            </Routes>
          </main>
        </div>
      </div>
    </UnitCtx.Provider>
  );
}
